import React, {useEffect, useState} from "react";
import {CalculationBuilderContext} from "src/components/calculation-builder/CalculationBuilderView";
import useReducerWithLogger from "src/services/utils/ReducerWithLogger";
import {jeSettingsReducer} from "src/services/calculation-builder/JESettingsReducer";
import {initialState, JESettingsState} from "src/services/calculation-builder/JESettingsState";
import {Alert, Box, Button, ExpandableSection, Header, SpaceBetween} from "@amzn/awsui-components-react";
import TPEAction from "src/models/common/TPEAction";
import ServiceCollection from "src/services/ServiceCollection";
import {CalculationSourceType} from "src/models/common/CalculationSource";
import CONSTANTS from "src/utils/constants";
import StringUtils from "src/utils/stringUtils";
import {useHistory, useParams} from "react-router-dom";
import {TPELoadingSpinner} from "src/components/shared/TPELoadingSpinner";

export type ContextType = {
    state: JESettingsState,
    dispatch: React.Dispatch<TPEAction>,
    services: ServiceCollection,
    calculationSource: CalculationSourceType
}

interface DownloadJETemplateProviderProps {
    state: JESettingsState;
    dispatch: React.Dispatch<TPEAction>;
    services: ServiceCollection;
    children: React.ReactNode;
    calculationSource: CalculationSourceType;
}

const DownloadJETemplateProvider = (props: DownloadJETemplateProviderProps) => {
    const {state, dispatch, services, children, calculationSource} = props;
    const providerValue = React.useMemo(() => ({
        state, dispatch, services, calculationSource
    }), [state, dispatch]);
    return (
        <DownloadJETemplateContext.Provider value={providerValue}>
            {children}
        </DownloadJETemplateContext.Provider>
    );
}
export const DownloadJETemplateContext = React.createContext(null as unknown as ContextType);

export default function DownloadJETemplateContainer(props: { expanded?: boolean , isICRSRecord: boolean, coaDataLoading?: boolean}) {
    const {expanded = false, isICRSRecord, coaDataLoading = false} = props;
    const {services, calcBuilderState} = React.useContext(CalculationBuilderContext);
    const history = useHistory();
    const [state, dispatch] = useReducerWithLogger(jeSettingsReducer, initialState);
    const {
        calculation,
        selectedCalculationVersion,
        viewMode,
        calculationSource,
        calculationExecution,
        isConfiguringJournals
    } = calcBuilderState;

    const JOURNAL_ENTRY_INSTRUCTIONS = {
        STEP_1: "Step 1: Download the journal entry template and fill in all required information for editable attributes.",
        STEP_2: "Step 2: Click Upload Journal Template to upload the finalized template and generate journal entries."
    };
    const [downloadJournals, setDownloadJournals] = useState(false);
    const [showInfoAlert, setShowInfoAlert] = useState(true);
    const [isViewModeEditable, setIsViewModeEditable] = useState(true);
    const [isExpanded, setIsExpanded] = useState(expanded);
    const { encodedCliRecordId } = useParams<{ encodedCliRecordId: string }>();
    const encodedParam = encodedCliRecordId === 'new' 
        ? btoa(isICRSRecord ? calculation?.calculationNumber || '' : calculation?.recordId || '')
        : encodedCliRecordId;
    const basePath = isICRSRecord ? '/icrs-calculations' : '/calculations';
    const configureJournalsPath = `${basePath}/builder/configureJournals/${encodedParam}`;
    // Combined loading state for step 4
    const step4Loading = coaDataLoading || isConfiguringJournals;
    
    const [journalEntryTemplateUrl, journalEntryTemplateUrlLoading, journalEntryTemplateUrlError] = services.journalsService.downloadJournals(
            downloadJournals, CONSTANTS.JOURNAL_LINE_TYPES.CONFIGURATION, 
            calculation?.calculationNumber, 
            calculationExecution?.calculationVersion == null ? selectedCalculationVersion : calculationExecution?.calculationVersion);
    
    useEffect(() => {
        // Sync internal expanded state with expanded prop
        setIsExpanded(expanded);
    }, [expanded]);
    
    useEffect(() => {
        if (StringUtils.isNullOrEmpty(journalEntryTemplateUrl)) {
            return;
        }
        try {
            const newWindow = window.open(journalEntryTemplateUrl as string);
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                // Popup was blocked
                services.messageService.showErrorBanner('Popup blocked. Please allow popups for this site.');
            }
        } catch (error) {
            services.messageService.showErrorBanner(`Failed to open download window: ${error}`);
        }
        setDownloadJournals(false);
    }, [journalEntryTemplateUrl])

    useEffect(() => {
        const editable = viewMode != CONSTANTS.VIEW_MODE.READ_ONLY && viewMode != CONSTANTS.VIEW_MODE.FROZEN;
        setIsViewModeEditable(editable);
    }, [viewMode])

    useEffect(() => {
        if (journalEntryTemplateUrlError != null && journalEntryTemplateUrlError != '') {
            services.messageService.showErrorBanner(`Failed to download journal template. ${journalEntryTemplateUrlError}`);
        }
    }, [journalEntryTemplateUrlError])

    return (
        <ExpandableSection className="polarisExpandableSection configureJESettingsSection" variant="container"
                           defaultExpanded={expanded}
                           onChange={({ detail }) => setIsExpanded(detail.expanded)}
                           header={
                               <Header
                                   actions={
                                       isExpanded && !step4Loading ? (
                                           <Button 
                                               variant="primary"
                                               disabled={!isViewModeEditable}
                                               onClick={() => history.push(configureJournalsPath, { calcBuilderState })}
                                           >
                                               Upload Journal Template
                                           </Button>
                                       ) : null
                                   }
                               >
                                   <h2>Step 4: Configure journal entries</h2>
                               </Header>
                           }
        >
            <DownloadJETemplateProvider services={services} state={state} dispatch={dispatch}
                                        calculationSource={calculationSource}>
                <TPELoadingSpinner loading={step4Loading}>
                    <SpaceBetween direction='vertical' size='s'>
                        {showInfoAlert && isViewModeEditable &&
                            <Alert type="info" dismissible onDismiss={() => {
                                setShowInfoAlert(false)
                            }}>
                                <React.Fragment>
                                    <span>Click the "Configure journal entries" button in step 3 to regenerate the journal entry template.</span>
                                    <br/>
                                </React.Fragment>
                            </Alert>
                        }
                        <div>
                            <p><strong>Instructions:</strong></p>
                            <p>{JOURNAL_ENTRY_INSTRUCTIONS.STEP_1}</p>
                            <p>{JOURNAL_ENTRY_INSTRUCTIONS.STEP_2}</p>
                        </div>

                        <Button
                            variant="inline-link"
                            iconName="download"
                            disabled={journalEntryTemplateUrlLoading}
                            loading={journalEntryTemplateUrlLoading}
                            onClick={() => {
                                if (!downloadJournals && !journalEntryTemplateUrlLoading) {
                                    setDownloadJournals(true)
                                }
                            }}
                        >
                            Download Journal Entry Template
                        </Button>
                        <Alert type="warning">
                            Only edit the seven segments, tax exemption code and line description override in excel. All
                            other attributes will be systematically overridden.
                        </Alert>
                    </SpaceBetween>
                </TPELoadingSpinner>
            </DownloadJETemplateProvider>
        </ExpandableSection>
    )
}
